// Shell behaviors:
//   - Mobile sidebar toggle / scrim click-to-close
//   - Desktop sidebar collapse toggle (icon-rail ↔ expanded), persisted via
//     cookie so the choice survives navigation. The toggle button is
//     auto-injected into every `.bf-shell` that has a `.bf-shell__sidebar`,
//     unless the shell opts out with `data-bf-sidebar-toggle="off"`.
//   - Sidebar submenu popout positioning. .bf-sidebar__nav has
//     overflow-y: auto for long nav lists, which clips the popout
//     panels of sidebar groups. Switch the popout to position: fixed
//     and compute coords from the group's bounding rect so it
//     escapes the clipping context. Only runs in rail mode — in
//     expanded mode the submenu is an inline drawer (CSS-only).

import { $$, on } from './util';

const COOKIE = 'bf-shell-prefs';
const CSRF_COOKIE = 'bapp_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const SAFE_HTTP_METHODS = new Set(['get', 'head', 'options', 'trace']);
const CONTENT_TARGET_SELECTOR = '#bf-content-body, [data-bf-content-target], #bf-main';
type Prefs = { sidebar?: 'rail' | 'expanded' };

type HtmxAPI = {
  ajax?: (verb: string, path: string, context: { target: Element; swap: string; push?: string }) => void;
};

type HtmxConfigRequestDetail = {
  verb?: string;
  headers?: Record<string, string>;
  requestConfig?: {
    verb?: string;
    method?: string;
  };
};

declare global {
  interface Window {
    htmx?: HtmxAPI;
  }
}

function readPrefs(): Prefs {
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE + '=([^;]+)'));
  if (!m || m[1] === undefined) return {};
  try { return JSON.parse(decodeURIComponent(m[1])) as Prefs; } catch { return {}; }
}
function writePrefs(p: Prefs): void {
  const v = encodeURIComponent(JSON.stringify(p));
  document.cookie = COOKIE + '=' + v + ';max-age=' + (60 * 60 * 24 * 365) + ';path=/;samesite=lax';
}

function readCookie(name: string): string {
  const prefix = name + '=';
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return '';
}

function bindHtmxCSRF(): void {
  document.addEventListener('htmx:configRequest', (event) => {
    const detail = (event as CustomEvent<HtmxConfigRequestDetail>).detail;
    if (!detail?.headers) return;
    const method = String(
      detail.verb || detail.requestConfig?.verb || detail.requestConfig?.method || '',
    ).toLowerCase();
    if (!method || SAFE_HTTP_METHODS.has(method)) return;
    const token = readCookie(CSRF_COOKIE);
    if (token) {
      detail.headers[CSRF_HEADER] = token;
    }
  });
}

function injectSidebarToggle(shell: HTMLElement): void {
  if (shell.dataset.bfSidebarToggle === 'off') return;
  const sidebar = shell.querySelector<HTMLElement>(':scope > .bf-shell__sidebar');
  if (!sidebar) return;
  if (sidebar.querySelector(':scope > .bf-shell__sidebar-toggle')) return;

  const btn = document.createElement('button');
  btn.className = 'bf-shell__sidebar-toggle';
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Toggle sidebar');
  btn.innerHTML = '<i class="fa-solid fa-chevron-right"></i>';
  on(btn, 'click', () => {
    const next = shell.dataset.bfSidebar === 'expanded' ? 'rail' : 'expanded';
    shell.dataset.bfSidebar = next;
    writePrefs({ ...readPrefs(), sidebar: next });
    // Clear any inline positioning the rail-mode hover handler
    // applied to submenus. Without this, switching from rail to
    // expanded keeps `position: fixed; top; left` set on the
    // submenu, which makes the popout linger over the inline
    // drawer the expanded layout wants.
    for (const submenu of $$<HTMLElement>('.bf-sidebar__submenu', shell)) {
      submenu.style.position = '';
      submenu.style.top = '';
      submenu.style.left = '';
    }
  });
  sidebar.appendChild(btn);
}

// Apply persisted prefs as early as init runs. Pages that prefer a specific
// state can pre-set `data-bf-sidebar` in HTML — that wins over the cookie.
function applyPrefs(shell: HTMLElement): void {
  if (shell.dataset.bfSidebar) return;
  const prefs = readPrefs();
  shell.dataset.bfSidebar =
    prefs.sidebar === 'expanded' || prefs.sidebar === 'rail' ? prefs.sidebar : 'rail';
}

// bindSidebarSubmenus pins each .bf-sidebar__group's popout to
// the viewport via position: fixed when the sidebar is in rail
// mode. Without this the popout is clipped by .bf-sidebar__nav's
// overflow-y: auto (which is needed for long nav lists). The CSS
// fallback (position: absolute) still renders correctly when JS
// is disabled — just clipped at the nav's right edge.
//
// In expanded mode the submenu becomes an inline drawer (CSS
// :hover does nothing structural) — clear inline coords so the
// CSS static positioning wins.
function bindSidebarSubmenus(shell: HTMLElement): void {
  for (const group of $$<HTMLElement>('.bf-sidebar__group', shell)) {
    if (group.dataset.bfSubmenuBound === 'true') continue;
    group.dataset.bfSubmenuBound = 'true';
    const submenu = group.querySelector<HTMLElement>(':scope > .bf-sidebar__submenu');
    if (!submenu) continue;

    const position = (): void => {
      if (shell.dataset.bfSidebar === 'expanded') {
        // Inline drawer — let CSS position: static take over.
        submenu.style.position = '';
        submenu.style.top = '';
        submenu.style.left = '';
        return;
      }
      const rect = group.getBoundingClientRect();
      submenu.style.position = 'fixed';
      submenu.style.top = `${rect.top}px`;
      submenu.style.left = `${rect.right}px`;
      // No inline overflow: an overflow-y on the submenu forces
      // overflow-x to clip too (CSS spec — non-visible on one
      // axis coerces the other to auto), which would chop off
      // the .bf-sidebar__submenu::before hover bridge that sits
      // a negative-x offset to bridge the gap to the trigger.
      // Tall popouts overflow past the viewport edge — addons
      // with very long submenu lists need to add their own
      // scroll wrapper instead.
    };

    on(group, 'mouseenter', position);
    on(group, 'focusin', position);
  }

  // Disclosure toggle — any element with data-bf-toggle="group"
  // flips data-bf-open on its closest .bf-sidebar__group, so the
  // inline drawer (expanded mode only — CSS hides the dedicated
  // toggle button in rail mode and is hover-only there) shows or
  // hides. Two markup shapes carry the attribute:
  //   - .bf-sidebar__group-toggle — the small chevron button
  //     emitted next to a parent that has its own URL (the <a>
  //     navigates, the toggle handles disclosure).
  //   - .bf-sidebar__item — when a parent has NO url, the whole
  //     row IS the toggle (it has nowhere else to navigate to).
  // aria-expanded mirrors the visual state on the trigger element.
  for (const toggle of $$<HTMLElement>('[data-bf-toggle="group"]', shell)) {
    if (toggle.dataset.bfGroupToggleBound === 'true') continue;
    toggle.dataset.bfGroupToggleBound = 'true';
    on(toggle, 'click', (e) => {
      e.preventDefault();
      const group = toggle.closest<HTMLElement>('.bf-sidebar__group');
      if (!group) return;
      const open = group.dataset.bfOpen === 'true';
      if (open) {
        delete group.dataset.bfOpen;
      } else {
        group.dataset.bfOpen = 'true';
      }
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  }
}

export function contentTarget(root: ParentNode = document): Element | null {
  return root.querySelector(CONTENT_TARGET_SELECTOR);
}

export function navigateContent(href: string, root: ParentNode = document): boolean {
  const target = contentTarget(root);
  if (!href || !target || typeof window.htmx?.ajax !== 'function') return false;
  window.htmx.ajax('GET', href, { target, swap: 'innerHTML', push: href });
  return true;
}

let globalsBound = false;

function bindGlobals(): void {
  if (globalsBound) return;
  globalsBound = true;
  bindHtmxCSRF();
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const row = target?.closest<HTMLElement>('[data-href]');
    if (!row) return;
    if (target?.closest('a, button, input, select, textarea, label')) return;
    const href = row.getAttribute('data-href') || '';
    if (!href) return;
    if (navigateContent(href)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    window.location.assign(href);
  });
  document.addEventListener('htmx:load', (event) => {
    const detail = (event as CustomEvent).detail as { elt?: ParentNode } | undefined;
    init(detail?.elt || document);
  });
  document.addEventListener('htmx:oobAfterSwap', () => {
    init(document);
  });
}

function shells(root: ParentNode): HTMLElement[] {
  const out: HTMLElement[] = [];
  if (root instanceof Element && root.matches('.bf-shell')) {
    out.push(root as HTMLElement);
  }
  out.push(...$$<HTMLElement>('.bf-shell', root));
  return out;
}

export function init(root: ParentNode = document): void {
  bindGlobals();
  for (const shell of shells(root)) {
    applyPrefs(shell);
    injectSidebarToggle(shell);
    bindSidebarSubmenus(shell);

    const open = () => { shell.dataset.bfShellOpen = 'true'; };
    const close = () => { shell.dataset.bfShellOpen = 'false'; };
    for (const t of $$<HTMLElement>('[data-bf-toggle="sidebar"]', shell)) {
      if (t.dataset.bfSidebarBound === 'true') continue;
      t.dataset.bfSidebarBound = 'true';
      on(t, 'click', () => {
        shell.dataset.bfShellOpen = shell.dataset.bfShellOpen === 'true' ? 'false' : 'true';
      });
    }
    for (const t of $$<HTMLElement>('[data-bf-shell-open]', shell)) {
      if (t.dataset.bfShellOpenBound === 'true') continue;
      t.dataset.bfShellOpenBound = 'true';
      on(t, 'click', open);
    }
    for (const t of $$<HTMLElement>('.bf-shell__scrim', shell)) {
      if (t.dataset.bfShellCloseBound === 'true') continue;
      t.dataset.bfShellCloseBound = 'true';
      on(t, 'click', close);
    }
    if (shell.dataset.bfEscapeBound !== 'true') {
      shell.dataset.bfEscapeBound = 'true';
      on(window, 'keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Escape') close();
      });
    }
  }
}
